import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateCustomPromotion,
  useCustomPromotions,
  useMenuProducts,
  useUpdateCustomPromotion,
} from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";
import type { UpsertCustomPromotionInput } from "@/types/menu";

const createEmptyFormState = (): UpsertCustomPromotionInput => ({
  name: "",
  slug: "",
  description: "",
  discount: 0,
  startDate: null,
  endDate: null,
  minQuantity: null,
  active: true,
  cron: null,
  productIds: [],
});

export default function CustomPromotionFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data: promotionsData } = useCustomPromotions(activeOrganization?.id);
  const { data: productsData } = useMenuProducts(activeOrganization?.id);
  const createPromotion = useCreateCustomPromotion();
  const updatePromotion = useUpdateCustomPromotion();
  const [formState, setFormState] = useState<UpsertCustomPromotionInput>(createEmptyFormState());

  const editingPromotion = id ? promotionsData?.promotions.find((p) => p.id === id) : undefined;

  useEffect(() => {
    if (editingPromotion) {
      setFormState({
        name: editingPromotion.name,
        slug: editingPromotion.slug,
        description: editingPromotion.description ?? "",
        discount: editingPromotion.discount,
        startDate: editingPromotion.startDate,
        endDate: editingPromotion.endDate,
        minQuantity: editingPromotion.minQuantity,
        active: editingPromotion.active,
        cron: editingPromotion.cron,
        productIds: editingPromotion.productIds ?? [],
      });
    }
  }, [editingPromotion]);

  const handleChange = <T extends keyof UpsertCustomPromotionInput>(
    field: T,
    value: UpsertCustomPromotionInput[T],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const handleProductToggle = (productId: string) => {
    setFormState((currentState) => {
      const currentIds = currentState.productIds ?? [];
      const nextIds = currentIds.includes(productId)
        ? currentIds.filter((pid) => pid !== productId)
        : [...currentIds, productId];
      return { ...currentState, productIds: nextIds };
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload: UpsertCustomPromotionInput = {
      name: formState.name?.trim() ?? "",
      slug: formState.slug?.trim() || undefined,
      description: formState.description?.trim() || null,
      discount: formState.discount ?? 0,
      startDate: formState.startDate || null,
      endDate: formState.endDate || null,
      minQuantity: formState.minQuantity ?? null,
      active: formState.active ?? true,
      cron: formState.cron?.trim() || null,
      productIds: formState.productIds ?? [],
    };

    if (!payload.name) {
      toast.error(t("menu.promotions.shared.notifications.nameRequired"));
      return;
    }

    if (id) {
      updatePromotion.mutate(
        { data: payload, id },
        {
          onError: (mutationError) => {
            toast.error(mutationError.message);
          },
          onSuccess: () => {
            toast.success(t("menu.promotions.shared.notifications.updated"));
            navigate("/menu/promotions/custom");
          },
        },
      );
      return;
    }

    createPromotion.mutate(payload, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.promotions.shared.notifications.created"));
        navigate("/menu/promotions/custom");
      },
    });
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.promotions.custom.title")}</CardTitle>
            <CardDescription>
              {t("menu.promotions.shared.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const formTitle = id
    ? t("menu.promotions.custom.form.editTitle")
    : t("menu.promotions.custom.form.createTitle");

  const submitLabel = id
    ? t("menu.promotions.shared.form.save")
    : t("menu.promotions.shared.form.create");

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">{formTitle}</h1>
        <p className="text-muted-foreground text-sm">
          {t("menu.promotions.custom.form.description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{formTitle}</CardTitle>
          <CardDescription>{t("menu.promotions.custom.form.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="custom-promo-name">{t("menu.promotions.shared.form.name")}</Label>
              <Input
                id="custom-promo-name"
                onChange={(event) => handleChange("name", event.target.value)}
                placeholder={t("menu.promotions.shared.form.namePlaceholder")}
                value={formState.name ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-slug">{t("menu.promotions.shared.form.slug")}</Label>
              <Input
                id="custom-promo-slug"
                onChange={(event) => handleChange("slug", event.target.value)}
                placeholder={t("menu.promotions.shared.form.slugPlaceholder")}
                value={formState.slug ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-description">
                {t("menu.promotions.shared.form.longDescription")}
              </Label>
              <Textarea
                id="custom-promo-description"
                onChange={(event) => handleChange("description", event.target.value)}
                placeholder={t("menu.promotions.shared.form.descriptionPlaceholder")}
                rows={4}
                value={formState.description ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-discount">
                {t("menu.promotions.shared.form.discount")}
              </Label>
              <Input
                id="custom-promo-discount"
                max={100}
                min={0}
                onChange={(event) => handleChange("discount", Number(event.target.value))}
                type="number"
                value={formState.discount ?? 0}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-start-date">
                {t("menu.promotions.shared.form.startDate")}
              </Label>
              <Input
                id="custom-promo-start-date"
                onChange={(event) =>
                  handleChange(
                    "startDate",
                    event.target.value ? new Date(event.target.value).toISOString() : null,
                  )
                }
                type="datetime-local"
                value={
                  formState.startDate
                    ? new Date(formState.startDate).toISOString().slice(0, 16)
                    : ""
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-end-date">
                {t("menu.promotions.shared.form.endDate")}
              </Label>
              <Input
                id="custom-promo-end-date"
                onChange={(event) =>
                  handleChange(
                    "endDate",
                    event.target.value ? new Date(event.target.value).toISOString() : null,
                  )
                }
                type="datetime-local"
                value={
                  formState.endDate ? new Date(formState.endDate).toISOString().slice(0, 16) : ""
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-min-quantity">
                {t("menu.promotions.shared.form.minQuantity")}
              </Label>
              <Input
                id="custom-promo-min-quantity"
                onChange={(event) =>
                  handleChange(
                    "minQuantity",
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
                type="number"
                value={formState.minQuantity ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="custom-promo-cron">{t("menu.promotions.shared.form.cron")}</Label>
              <Input
                id="custom-promo-cron"
                onChange={(event) => handleChange("cron", event.target.value || null)}
                placeholder={t("menu.promotions.shared.form.cronPlaceholder")}
                value={formState.cron ?? ""}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">{t("menu.promotions.shared.form.active")}</p>
                <p className="text-muted-foreground text-sm">
                  {t("menu.promotions.shared.form.activeDescription")}
                </p>
              </div>
              <Switch
                checked={formState.active ?? true}
                onCheckedChange={(checked) => handleChange("active", checked)}
              />
            </div>

            <div className="grid gap-2">
              <Label>{t("menu.promotions.custom.form.selectProducts")}</Label>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border p-3">
                {productsData?.products.length ? (
                  productsData.products.map((product) => (
                    <label className="flex items-center gap-2 text-sm" key={product.id}>
                      <input
                        checked={formState.productIds?.includes(product.id) ?? false}
                        onChange={() => handleProductToggle(product.id)}
                        type="checkbox"
                      />
                      {product.name}
                    </label>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {t("menu.promotions.custom.form.noProducts")}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                disabled={createPromotion.isPending || updatePromotion.isPending}
                type="submit"
              >
                {submitLabel}
              </Button>
              <Button
                onClick={() => navigate("/menu/promotions/custom")}
                type="button"
                variant="outline"
              >
                {t("menu.promotions.shared.form.cancel")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
